/**
 * Pricing rules shared by the athlete app, the brand app and the server: rate card ranges by
 * level, licensing rates, gifting performance bonuses and the gifting activation fee.
 *
 * The suggestions are market-based starting points, not prices anyone has to take: athletes
 * accept a suggested range or set their own rates, and brands accept suggested bonuses or set
 * their own. Tune the numbers here; every screen follows.
 */
import type { Level } from './athleteScore'

// ── Rate card ─────────────────────────────────────────────────────────────────

export type DeliverableId = 'ig_reel' | 'tiktok_video' | 'ig_story' | 'static_post' | 'appearance'

export const DELIVERABLES: {
  id: DeliverableId
  label: string
  unit: string
  /** One line to explain it to an athlete */
  hint: string
}[] = [
  {
    id: 'ig_reel',
    label: 'Instagram Reel',
    unit: 'per Reel',
    hint: 'A 15–60 second video on your Instagram.',
  },
  {
    id: 'tiktok_video',
    label: 'TikTok video',
    unit: 'per video',
    hint: 'A video posted to your TikTok.',
  },
  {
    id: 'ig_story',
    label: 'Instagram Story',
    unit: 'per set of 3',
    hint: 'Three Story frames, with a link if the brand wants one.',
  },
  {
    id: 'static_post',
    label: 'Static post',
    unit: 'per post',
    hint: 'A photo or carousel on your feed.',
  },
  {
    id: 'appearance',
    label: 'Appearance',
    unit: 'per hour',
    hint: 'Showing up in person: events, signings, clinics.',
  },
]

/**
 * Typical price ranges by level, in US dollars. Built from what brands pay college and
 * emerging creators per deliverable (roughly $5–15 per 1,000 followers for video, less for
 * Stories and photos), rounded to ranges a brand can say yes to quickly.
 */
export const RATE_RANGES: Record<Level, Record<DeliverableId, [number, number]>> = {
  L1: {
    ig_reel: [75, 150],
    tiktok_video: [75, 150],
    ig_story: [40, 80],
    static_post: [60, 120],
    appearance: [75, 150],
  },
  L2: {
    ig_reel: [150, 300],
    tiktok_video: [150, 300],
    ig_story: [80, 150],
    static_post: [120, 250],
    appearance: [150, 250],
  },
  L3: {
    ig_reel: [300, 600],
    tiktok_video: [300, 600],
    ig_story: [150, 300],
    static_post: [250, 450],
    appearance: [250, 450],
  },
  L4: {
    ig_reel: [600, 1200],
    tiktok_video: [600, 1200],
    ig_story: [300, 500],
    static_post: [450, 800],
    appearance: [450, 750],
  },
}

// ── Licensing ─────────────────────────────────────────────────────────────────

/**
 * Every deal includes 30 days of paid-ad use of the content: it is part of the rate-card price,
 * not a separate agreement. Beyond that a brand extends the license, and the longer the
 * extension the lower the monthly rate.
 */
export const INCLUDED_LICENSE_DAYS = 30

/** Extension lengths, beyond the included 30 days */
export type LicenseTerm = 30 | 60 | 90 | 180
export const LICENSE_TERMS: {
  days: LicenseTerm
  label: string
  months: number
  /** Suggested discount on the monthly rate */
  discount: number
}[] = [
  { days: 30, label: '+30 days', months: 1, discount: 0 },
  { days: 60, label: '+60 days', months: 2, discount: 0.1 },
  { days: 90, label: '+90 days', months: 3, discount: 0.15 },
  { days: 180, label: '+6 months', months: 6, discount: 0.25 },
]

/** A month's extension, as a share of the athlete's video price (one asset as a paid ad) */
export const LICENSE_MONTHLY_SHARE = 0.3

const roundTo = (n: number, step: number) => Math.max(step, Math.round(n / step) * step)

/** The suggested price per asset to extend by each term, from the athlete's video price */
export function suggestedLicensing(videoPrice: number): Record<LicenseTerm, number> {
  const month = videoPrice * LICENSE_MONTHLY_SHARE
  return Object.fromEntries(
    LICENSE_TERMS.map((t) => [t.days, roundTo(month * t.months * (1 - t.discount), 5)]),
  ) as Record<LicenseTerm, number>
}

/** How much cheaper per month a longer extension is than extending a month at a time (0–1) */
export function licenseSavings(prices: Record<LicenseTerm, number>, days: LicenseTerm) {
  const t = LICENSE_TERMS.find((x) => x.days === days)!
  const monthly = prices[30]
  if (!monthly || t.months === 1) return 0
  return Math.max(0, 1 - prices[days] / (monthly * t.months))
}

// ── Content themes ────────────────────────────────────────────────────────────

/**
 * The recurring formats an athlete posts, each tied to one of the 24 content categories
 * (@adthlete/common/utils/contentCategories). Athletes pick theirs on the rate card; brands book a
 * deliverable in one of these at rate-card prices. Anything else (an unboxing, a product review)
 * is a custom deliverable, off the rate card.
 */
export const CONTENT_THEMES: { id: string; label: string; category: string }[] = [
  { id: 'grwm', label: 'Get ready with me', category: 'beauty_grooming' },
  { id: 'come_along', label: 'Come along with me', category: 'lifestyle' },
  { id: 'day_in_life', label: 'Day in my life', category: 'lifestyle' },
  { id: 'routine', label: 'Morning or night routine', category: 'recovery_wellness' },
  { id: 'train_with_me', label: 'Train with me', category: 'training_fitness' },
  { id: 'cook_with_me', label: 'Cook with me', category: 'food_dining' },
  { id: 'what_i_eat', label: 'What I eat in a day', category: 'nutrition' },
  { id: 'recovery', label: 'Recovery routine', category: 'recovery_wellness' },
  { id: 'game_day', label: 'Game day', category: 'sport_competition' },
  { id: 'fit_check', label: 'Fit check', category: 'fashion_style' },
  { id: 'study_with_me', label: 'Study with me', category: 'education_career' },
  { id: 'skits', label: 'Skits and comedy', category: 'humor_comedy' },
  { id: 'storytime', label: 'Storytime or Q&A', category: 'entertainment_pop_culture' },
  { id: 'mindset', label: 'Mindset and motivation', category: 'motivation_mindset' },
]

/** One of the athlete's own posts that shows a theme: proof for brands ("show me a fit check") */
export type ThemeExample = {
  /** The post; for a still uploaded without a post yet, the image itself */
  url: string
  thumbnail: string | null
  platform: string | null
  views: number | null
  caption: string | null
  /** The thumbnail is a still uploaded for the card (from the shoot), kept as is */
  uploaded?: boolean
}

export type ContentTheme = {
  id: string
  label: string
  category: string
  /** The athlete's example post for this theme, if they added one */
  example?: ThemeExample | null
}

/** How many themes an athlete can list on their rate card */
export const MAX_THEMES = 5

// ── The athlete's rate card ───────────────────────────────────────────────────

export type RateCardEntry = {
  low: number
  high: number
  /** True when the athlete set their own price rather than taking the suggested range */
  custom: boolean
}

/**
 * Something else the athlete sells, priced by them: an autograph signing, a camp visit, a
 * podcast mention. Shown in their Content Shop next to the series, bookable in one tap.
 */
export type RateCardItem = {
  id: string
  label: string
  description: string
  /** How it's priced: 'each', 'per hour' or 'per event' */
  unit: ItemUnit
  low: number
  high: number
}
export const ITEM_UNITS = ['each', 'per hour', 'per event'] as const
export type ItemUnit = (typeof ITEM_UNITS)[number]
/** How many of their own items a shop can list */
export const MAX_ITEMS = 8

export type RateCard = {
  /** The level the suggestions were based on when the athlete saved */
  level: Level
  /** The recurring formats they post, which brands can book at these rates */
  themes?: ContentTheme[]
  /** Their own items, priced by them */
  items?: RateCardItem[]
  deliverables: Record<DeliverableId, RateCardEntry>
  licensing: Record<LicenseTerm, { amount: number; custom: boolean }>
  updatedAt: string
}

/** A rate card made entirely of suggestions for this level */
export function suggestedRateCard(level: Level): RateCard {
  const ranges = RATE_RANGES[level]
  const deliverables = Object.fromEntries(
    DELIVERABLES.map((d) => [
      d.id,
      { low: ranges[d.id][0], high: ranges[d.id][1], custom: false },
    ]),
  ) as Record<DeliverableId, RateCardEntry>
  const video = (ranges.ig_reel[0] + ranges.ig_reel[1]) / 2
  const lic = suggestedLicensing(video)
  return {
    level,
    deliverables,
    licensing: Object.fromEntries(
      LICENSE_TERMS.map((t) => [t.days, { amount: lic[t.days], custom: false }]),
    ) as RateCard['licensing'],
    updatedAt: new Date().toISOString(),
  }
}

/** The video price used to suggest licensing: the midpoint of their Reel rate */
export const videoPriceOf = (card: Pick<RateCard, 'deliverables'>) => {
  const reel = card.deliverables.ig_reel
  return (reel.low + reel.high) / 2
}

/** "$150–300" or "$250" */
export const formatRange = (e: { low: number; high: number }) =>
  e.low === e.high
    ? `$${e.low.toLocaleString('en-US')}`
    : `$${e.low.toLocaleString('en-US')}–${e.high.toLocaleString('en-US')}`

/** The lowest price on the card, for "From $75" in the Marketplace */
export const fromPrice = (card: Pick<RateCard, 'deliverables'>) =>
  Math.min(...Object.values(card.deliverables).map((e) => e.low))

// ── Gifting: activation fee, usage rights, performance bonuses ────────────────

/** What Athios charges the brand per gifted athlete */
export const GIFTING_ACTIVATION_FEE = 25

/** Every gifting post comes with this much paid-social usage */
export const GIFTING_USAGE_DAYS = 30

/** The view milestones a gifting post can unlock, in order */
export const BONUS_TIERS = [10_000, 25_000, 100_000, 250_000, 500_000, 1_000_000]

/**
 * How suggestions are worked out: paid social typically costs a brand about $10 per 1,000
 * views (CPM). Each milestone pays the athlete a share of the media value of the views it
 * adds, a larger share early (when a small creator over-delivers) and a smaller share as views
 * climb, so the total stays meaningful but never runs away. At 1M views the brand pays about
 * $2 per 1,000 views in bonuses, well under what the same reach costs in ads.
 */
export const BONUS_CPM = 10
const BONUS_SHARE = [0.5, 0.5, 0.35, 0.25, 0.2, 0.15]

export type BonusTier = { views: number; amount: number }

export function suggestedBonuses(): BonusTier[] {
  return BONUS_TIERS.map((views, i) => {
    const added = views - (BONUS_TIERS[i - 1] ?? 0)
    return { views, amount: roundTo((added / 1000) * BONUS_CPM * BONUS_SHARE[i], 5) }
  })
}

/** The milestones a post has reached, and what they add up to (bonuses are cumulative) */
export function unlockedBonuses(views: number, tiers: BonusTier[]) {
  const sorted = [...tiers].sort((a, b) => a.views - b.views)
  const unlocked = sorted.filter((t) => views >= t.views)
  const next = sorted.find((t) => views < t.views) ?? null
  return {
    unlocked,
    total: unlocked.reduce((s, t) => s + t.amount, 0),
    next,
    max: sorted.reduce((s, t) => s + t.amount, 0),
  }
}

/** "10K", "1M" */
export const viewsLabel = (n: number) =>
  n >= 1_000_000 ? `${n / 1_000_000}M` : n >= 1000 ? `${n / 1000}K` : String(n)

/** The terms a gifting deal carries, set on the campaign and copied onto each deal */
export type GiftingTerms = {
  activationFee: number
  usageDays: number
  bonuses: BonusTier[]
}

export const defaultGiftingTerms = (bonuses?: BonusTier[] | null): GiftingTerms => ({
  activationFee: GIFTING_ACTIVATION_FEE,
  usageDays: GIFTING_USAGE_DAYS,
  bonuses: bonuses?.length ? bonuses : suggestedBonuses(),
})
